import React, { useEffect, useState } from "react";
import {
  Users,
  FileSignature,
  Inbox,
  DollarSign,
  IdCard,
  Stethoscope,
  TrendingUp,
  TrendingDown,
  Loader2,
  Building2,
  UserRound,
  FileText,
  FolderLock,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

import { getDashboardStats, getRecentActivity } from "@/lib/api";
import ChatWidget from "@/components/ChatWidget";

const WIDGETS = [
  {
    key: "active_clients",
    label: "Active Clients",
    icon: Users,
    nav: "facility",
    deltaKind: "count",
    fmt: (v) => Number(v || 0).toLocaleString(),
    accent: "#0B2545",
    bg: "rgba(11,37,69,0.08)",
  },
  {
    key: "contracts_pending",
    label: "Contracts Pending",
    icon: FileSignature,
    nav: "contracts",
    deltaKind: "count",
    fmt: (v) => Number(v || 0).toLocaleString(),
    accent: "#7A5E12",
    bg: "rgba(212,175,55,0.18)",
  },
  {
    key: "new_inquiries",
    label: "New Inquiries",
    icon: Inbox,
    nav: "facility",
    deltaKind: "count",
    fmt: (v) => Number(v || 0).toLocaleString(),
    accent: "#0F6D5D",
    bg: "rgba(15,109,93,0.10)",
  },
  {
    key: "monthly_revenue",
    label: "Monthly Revenue",
    icon: DollarSign,
    nav: "consumer",
    deltaKind: "pct",
    fmt: (v) =>
      "$" +
      Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    accent: "#0B2545",
    bg: "rgba(11,37,69,0.08)",
  },
  {
    key: "staff_available",
    label: "Staff Available",
    icon: IdCard,
    nav: "operations",
    deltaKind: "label",
    fmt: (v) => Number(v || 0).toLocaleString(),
    accent: "#7A5E12",
    bg: "rgba(212,175,55,0.18)",
  },
  {
    key: "open_requests",
    label: "Open Requests",
    icon: Stethoscope,
    nav: "consumer",
    deltaKind: "count",
    fmt: (v) => Number(v || 0).toLocaleString(),
    accent: "#9B1C1C",
    bg: "rgba(155,28,28,0.08)",
  },
];

const ACTIVITY_META = {
  facility_lead: { icon: Building2, color: "#0B2545", bg: "rgba(11,37,69,0.08)" },
  consumer_lead: { icon: UserRound, color: "#0F6D5D", bg: "rgba(15,109,93,0.10)" },
  document: { icon: FileText, color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  staff_document: { icon: FolderLock, color: "#475569", bg: "rgba(71,85,105,0.10)" },
};

const formatRelative = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
};

const WidgetCard = ({ widget, data, onClick }) => {
  const Icon = widget.icon;
  const value = widget.fmt(data?.value);
  let deltaText = data?.delta_label || "";
  let deltaIcon = null;
  let deltaColor = "#475569";
  if (widget.deltaKind === "pct" && data?.delta_pct != null) {
    const up = data.delta_pct >= 0;
    deltaIcon = up ? TrendingUp : TrendingDown;
    deltaColor = up ? "#0F6D5D" : "#9B1C1C";
    deltaText = `${up ? "+" : ""}${data.delta_pct}% ${data.delta_label || ""}`;
  } else if (widget.deltaKind === "count" && data?.delta != null) {
    deltaText = `+${data.delta} ${data.delta_label || ""}`;
    if (data.delta > 0) deltaColor = "#0F6D5D";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`widget-${widget.key}`}
      className="group relative text-left rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5"
      style={{ borderColor: "#E2E8F0" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 12px 32px -16px rgba(11,37,69,0.25)";
        e.currentTarget.style.borderColor = "#D4AF37";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "#E2E8F0";
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <span
          className="grid place-items-center h-11 w-11 rounded-xl"
          style={{ background: widget.bg, color: widget.accent }}
        >
          <Icon className="h-5 w-5" />
        </span>
        <ArrowUpRight
          className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: "#D4AF37" }}
        />
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-1.5" style={{ color: "#64748B" }}>
        {widget.label}
      </div>
      <div className="font-display text-3xl font-bold mb-2" style={{ color: "#0B2545" }} data-testid={`widget-${widget.key}-value`}>
        {value}
      </div>
      <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: deltaColor }}>
        {deltaIcon ? React.createElement(deltaIcon, { className: "h-3.5 w-3.5" }) : null}
        <span>{deltaText}</span>
      </div>
    </button>
  );
};

const MonthlyOverview = ({ overview }) => {
  if (!overview) return null;
  const series = overview.series || [];
  const max = Math.max(1, ...series.map((s) => s.amount));
  const fmtMoney = (n) =>
    "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E2E8F0" }} data-testid="monthly-overview-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}>Monthly Overview</h3>
          <p className="text-xs mt-0.5" style={{ color: "#64748B" }}>Last 30 days of activity</p>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.2em] font-medium px-2.5 py-1 rounded-full"
          style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
        >
          Live
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Revenue", value: fmtMoney(overview.revenue) },
          { label: "Services Completed", value: overview.services_completed || 0 },
          { label: "New Clients", value: overview.new_clients || 0 },
          { label: "Total Inquiries", value: overview.inquiries || 0 },
        ].map((m) => (
          <div key={m.label} className="rounded-xl p-3" style={{ background: "#F8FAFC" }}>
            <div className="text-[10px] uppercase tracking-[0.16em] font-medium mb-1" style={{ color: "#64748B" }}>
              {m.label}
            </div>
            <div className="font-display text-xl font-bold" style={{ color: "#0B2545" }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-3" style={{ color: "#64748B" }}>
          Daily Revenue
        </div>
        {series.length === 0 ? (
          <div className="h-32 rounded-xl border-2 border-dashed grid place-items-center text-sm"
               style={{ borderColor: "#E2E8F0", color: "#94A3B8" }}>
            No paid services in the last 30 days yet.
          </div>
        ) : (
          <div className="h-32 flex items-end gap-1">
            {series.map((p) => (
              <div
                key={p.date}
                className="flex-1 rounded-t transition-all hover:opacity-80"
                style={{
                  height: `${Math.max(6, (p.amount / max) * 100)}%`,
                  background: "linear-gradient(180deg, #D4AF37 0%, #0B2545 100%)",
                }}
                title={`${p.date}: ${fmtMoney(p.amount)}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RecentActivity = ({ events }) => {
  return (
    <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E2E8F0" }} data-testid="recent-activity-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}>Recent Activity</h3>
          <p className="text-xs mt-0.5" style={{ color: "#64748B" }}>Latest events across the platform</p>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="h-32 rounded-xl border-2 border-dashed grid place-items-center text-sm"
             style={{ borderColor: "#E2E8F0", color: "#94A3B8" }}>
          No recent activity yet.
        </div>
      ) : (
        <ol className="space-y-3" data-testid="recent-activity-list">
          {events.map((e, idx) => {
            const meta = ACTIVITY_META[e.kind] || ACTIVITY_META.consumer_lead;
            const Icon = meta.icon;
            return (
              <li key={`${e.kind}-${idx}-${e.ts}`} className="flex items-start gap-3">
                <span
                  className="grid place-items-center h-9 w-9 rounded-full shrink-0"
                  style={{ background: meta.bg, color: meta.color }}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0 pt-1">
                  <div className="text-sm leading-snug" style={{ color: "#0B2545" }}>{e.title}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#94A3B8" }}>{formatRelative(e.ts)}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default function DashboardHome({ onNavigate, onSessionExpired, user, chatUnread = 0 }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([getDashboardStats(), getRecentActivity(10)]);
      setStats(s);
      setEvents(a.events || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        onSessionExpired?.();
        return;
      }
      toast.error("Couldn't load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-32">
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#0B2545" }} />
      </div>
    );
  }

  return (
    <div className="space-y-7" data-testid="dashboard-home">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>
            Dashboard
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
            Welcome back{user ? `, ${user.name}` : ""}.
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
            Here's what's happening across T&amp;J Clinical Solutions today.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="widgets-grid">
        {WIDGETS.map((w) => (
          <WidgetCard
            key={w.key}
            widget={w}
            data={stats?.widgets?.[w.key]}
            onClick={() => onNavigate?.(w.nav)}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3">
          <MonthlyOverview overview={stats?.monthly_overview} />
        </div>
        <div className="lg:col-span-2 space-y-5">
          <ChatWidget onOpenChat={() => onNavigate?.("chat")} unread={chatUnread} />
          <RecentActivity events={events} />
        </div>
      </div>
    </div>
  );
}
